import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Tab } from '@headlessui/react';
import { toast } from 'sonner';

import accountsAPI from '@/api/accounts';
import filesAPI from '@/api/files';
import organisationsAPI from '@/api/organisations';
import Button, { BUTTON_KIND } from '@/components/Button';
import { InfoPanelContainer } from '@/components/InfoPanelContainer';
import InfoPanelDivider from '@/components/InfoPanelDivider';
import InfoPanelFooter from '@/components/InfoPanelFooter';
import Input from '@/components/Input';
import Layout from '@/components/Layout';
import Modal from '@/components/Modals/Modal';
import InvalidPlanBanner from '@/components/SettingsPage/InvalidPlanBanner';
import { PlanRequiredModal } from '@/components/SettingsPage/ManagePlan/PlanManagement';
import ManagePlanTab from '@/components/SettingsPage/ManagePlanTab';
import TabList from '@/components/TabList';
import ToggleField from '@/components/ToggleField';
import ToggleFieldWithInfo from '@/components/ToggleFieldWithInfo';
import MESSAGES from '@/constants/messages-en';
import { IntegrationSettings } from '@/containers/IntegrationSettings';
import OrganisationSettings from '@/containers/OrganisationSettings';
import useAppState, { SettingsTab } from '@/hooks/useAppState';
import useAuth from '@/hooks/useAuth';
import useSubscriptionCheckoutUrl from '@/hooks/useSubscriptionCheckoutUrl';
import useSubscriptionStatus from '@/hooks/useSubscriptionStatus';
import IFile from '@/types/IFile';
import { IOrganisationUser } from '@/types/IOrganisation';
import SubscriptionStatus from '@/types/SubscriptionStatus';

export default function AccountSettingsPage() {
  const { user, orgID, getCurrentUser, userRole } = useAuth();
  const { settingsTab, selectSettingsTab } = useAppState();
  const location = useLocation();

  const { isInactive } = useSubscriptionStatus();
  const { checkoutUrl } = useSubscriptionCheckoutUrl();
  const [isPlanRequiredModalOpen, setIsPlanRequiredModalOpen] = useState(isInactive);

  // Derive tab disabling logic locally
  const shouldDisableTabs = isInactive;

  const tabs = useMemo(
    () => [
      {
        name: 'General',
        value: 'general',
        current: true,
        disabled: shouldDisableTabs,
      },
      {
        name: 'Organisation',
        value: 'organisation',
        current: false,
        disabled: shouldDisableTabs,
      },
      {
        name: 'Integrations',
        value: 'integrations',
        current: false,
        disabled: shouldDisableTabs,
      },
      {
        name: 'Preferences',
        value: 'profile-editing',
        current: false,
        disabled: shouldDisableTabs,
      },
      {
        name: 'Manage Plan',
        value: 'manage-plan',
        current: false,
      },
    ],
    [shouldDisableTabs],
  );

  const defaultTab = shouldDisableTabs
    ? SettingsTab.MANAGE_PLAN
    : tabs[settingsTab] && !tabs[settingsTab].disabled
    ? settingsTab
    : SettingsTab.GENERAL;
  const [selectedIndex, setSelectedIndex] = useState(defaultTab);
  const [tab, setTab] = useState(tabs[defaultTab].value);

  useEffect(() => {
    let tabIndex = settingsTab;
    const tab = tabs[settingsTab];
    if (!tab || tab.disabled) {
      tabIndex = defaultTab;
    }

    setTab(tabs[tabIndex].value);
    setSelectedIndex(tabIndex);
  }, [settingsTab, tabs]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'plan') {
      selectSettingsTab(SettingsTab.MANAGE_PLAN);
    } else if (hash === 'integrations') {
      selectSettingsTab(SettingsTab.INTEGRATIONS);
    }
  }, [location.hash]);

  useEffect(() => {
    if (
      user &&
      user.platform_subscription_status === SubscriptionStatus.INACTIVE
    ) {
      selectSettingsTab(SettingsTab.MANAGE_PLAN);
      setSelectedIndex(SettingsTab.MANAGE_PLAN);
      setTab(tabs[SettingsTab.MANAGE_PLAN].value);
    }
  }, [user]);

  const [firstName, setFirstName] = useState<string>(user?.first_name || '');
  const [lastName, setLastName] = useState<string>(user?.last_name || '');
  const [email, setEmail] = useState<string>(user?.email || '');
  const [mfaEnabled, setMfaEnabled] = useState(
    user?.authentication_type === 'mfa_email',
  );
  const [organisationName, setOrganisationName] = useState<string>('');
  const [consentStatement, setConsentStatement] = useState<IFile>();
  const [resetPasswordIsOpen, setResetPasswordIsOpen] =
    useState<boolean>(false);
  const [inviteUserEmail, setInviteUserEmail] = useState<string>('');
  const [inviteUserRole, setInviteUserRole] = useState<string>('org_admin');
  const [orgUsers, setOrgUsers] = useState<IOrganisationUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);
  // Consolidated preference settings state
  const [preferenceSettings, setPreferenceSettings] = useState({
    current: {
      isCardholderEditingEnabled: true,
      isTwoWayEmailEnabled: false,
    },
    original: {
      isCardholderEditingEnabled: true,
      isTwoWayEmailEnabled: false,
    },
  });

  async function saveGeneralSettings() {
    try {
      await accountsAPI.updateCurrentUser({
        first_name: firstName,
        last_name: lastName,
      });

      toast.success(MESSAGES.account.settings.success);
      getCurrentUser();
    } catch (error: unknown) {
      console.error(error);
    }
  }

  // Consolidated save function for both preference settings
  const savePreferenceSettings = () => {
    setLoading(true);

    if (orgID) {
      organisationsAPI
        .updateOrganisationSettings(orgID, {
          organisation_setting: {
            is_cardholder_editing_enabled:
              preferenceSettings.current.isCardholderEditingEnabled,
            is_contact_exchange_follow_up_email_enabled:
              preferenceSettings.current.isTwoWayEmailEnabled,
          },
        })
        .then(() => {
          toast.success(MESSAGES.organisation.settings);

          // Update original values after successful save
          setPreferenceSettings(prev => ({
            ...prev,
            original: { ...prev.current },
          }));
        })
        .catch(() =>
          toast.error('Failed to update preference settings.', {
            description: 'Please try again.',
          }),
        )
        .finally(() => setLoading(false));
    }
  };

  // Function to discard changes and reset to original values
  const discardPreferenceChanges = () => {
    setPreferenceSettings(prev => ({
      ...prev,
      current: { ...prev.original },
    }));
  };

  // Helper functions for updating individual preference settings
  const updateCardholderEditingEnabled = (enabled: boolean) => {
    setPreferenceSettings(prev => ({
      ...prev,
      current: { ...prev.current, isCardholderEditingEnabled: enabled },
    }));
  };

  const updateTwoWayEmailEnabled = (enabled: boolean) => {
    setPreferenceSettings(prev => ({
      ...prev,
      current: { ...prev.current, isTwoWayEmailEnabled: enabled },
    }));
  };

  const handleResetPassword = () => {
    setPasswordLoading(true);
    accountsAPI
      .forgotPassword({ forgot: { email } })
      .then(() => toast.success(MESSAGES.account.reset.success))
      .catch(() =>
        toast.error('Something went wrong.', {
          description: 'Please try again later',
        }),
      )
      .then(() => setPasswordLoading(false))
      .finally(() => setResetPasswordIsOpen(false));
  };

  const showOrganisationSettings = useCallback(() => {
    if (orgID) {
      setLoading(true);
      organisationsAPI
        .showOrganisationSettings(orgID)
        .then(res => {
          const data = res?.data?.data;
          // Type-safe handling with default values
          const cardholderEditingEnabled =
            data?.is_cardholder_editing_enabled ?? true;
          const twoWayEmailEnabled =
            data?.is_contact_exchange_follow_up_email_enabled ?? false;

          // Set both current and original values from API response
          setPreferenceSettings({
            current: {
              isCardholderEditingEnabled: cardholderEditingEnabled,
              isTwoWayEmailEnabled: twoWayEmailEnabled,
            },
            original: {
              isCardholderEditingEnabled: cardholderEditingEnabled,
              isTwoWayEmailEnabled: twoWayEmailEnabled,
            },
          });
        })
        .finally(() => {
          setLoading(false);
          if (location && location.state) {
            const { fromPage } = location.state;
            if (location.state.company_consent_file_id) {
              filesAPI
                .showFile(orgID, Number(location.state.company_consent_file_id))
                .then(res => setConsentStatement(res.data.data))
                .finally(() => {});
            }
          }
        });
    }
  }, [orgID, location]); // eslint-disable-next-line react-hooks/exhaustive-deps -- location.state changes are handled by location object identity

  // Helper to determine if invalid plan banner should be shown
  const showInvalidPlanBanner = useMemo(
    () => selectedIndex === SettingsTab.MANAGE_PLAN && isInactive,
    [selectedIndex, isInactive],
  );

  useEffect(() => {
    showOrganisationSettings();
  }, [showOrganisationSettings]);

  const handleMfaEnabledChange = (enabled: boolean) => {
    setLoading(true);
    setMfaEnabled(enabled);
    accountsAPI
      .updateCurrentUser({
        authentication_type: enabled ? 'mfa_email' : 'password',
      })
      .then(() => toast.success(MESSAGES.account.settings.success))
      .then(() => getCurrentUser())
      .finally(() => setLoading(false));
  };

  return (
    <Layout
      pageName="Settings"
      className="bg-gray-50"
      rightTitle={
        showInvalidPlanBanner ? (
          <InvalidPlanBanner
            onLearnMoreClick={() => setIsPlanRequiredModalOpen(true)}
          />
        ) : undefined
      }
    >
      {userRole === 'org_admin' && (
        <Tab.Group
          defaultIndex={settingsTab}
          selectedIndex={selectedIndex}
          onChange={(index: number) => {
            setSelectedIndex(index);
            setTab(tabs[index].value);
            selectSettingsTab(index);
            setLoading(false);
          }}
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full md:border-b border-gray-200">
            <div className="pt-4 md:pt-0 w-full md:w-auto">
              <TabList tabs={tabs} value={tab} />
            </div>
          </div>
          <Tab.Panels>
            <Tab.Panel className="outline-none">
              <Modal
                isOpen={resetPasswordIsOpen}
                setIsOpen={setResetPasswordIsOpen}
                dialogTitle="Reset password"
                dialogDescription="This will send an email with instructions to reset your password."
                successButtonText="Reset password"
                onSuccess={handleResetPassword}
                isLoading={passwordLoading}
              />
              <div className="mt-10">
                <InfoPanelContainer
                  title="Account settings"
                  footerContent={
                    <Button
                      buttonText="Save"
                      loading={loading}
                      onClick={saveGeneralSettings}
                    />
                  }
                >
                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 sm:col-span-3">
                      <Input
                        label="First name"
                        type="text"
                        value={firstName}
                        onChange={setFirstName}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <Input
                        label="Last Name"
                        type="text"
                        value={lastName}
                        onChange={setLastName}
                      />
                    </div>
                    <div className="mt-3 col-span-8 sm:col-span-4">
                      <Input
                        label="Email Address"
                        type="text"
                        value={email}
                        onChange={setEmail}
                        disabled
                      />
                    </div>
                  </div>
                </InfoPanelContainer>
                <InfoPanelDivider />
                <InfoPanelContainer title="Account security">
                  <div className="grid grid-cols-1 gap-6">
                    <ToggleFieldWithInfo
                      label="Multi-factor authentication"
                      enabled={mfaEnabled}
                      setter={handleMfaEnabledChange}
                      modalTitle="What is multi-factor authentication?"
                      modalDescription="mfa_content"
                    />
                  </div>
                  <div className="mt-5 flex flex-col lg:flex-row items-start lg:items-center justify-between">
                    <div className="min-w-0 text-sm leading-5 font-medium text-gray-900 mt-0.5">
                      Reset Password
                    </div>
                    <div className="inline-flex flex-col lg:flex-row lg:space-x-6 items-start lg:items-center mt-5 sm:mt-0">
                      <Button
                        kind={BUTTON_KIND.WHITE}
                        buttonText="Reset"
                        onClick={() => setResetPasswordIsOpen(true)}
                      />
                    </div>
                  </div>
                </InfoPanelContainer>
              </div>
            </Tab.Panel>
            <Tab.Panel className="outline-none">
              {orgID && (
                <OrganisationSettings
                  orgID={orgID}
                  organisationName={organisationName}
                  setOrganisationName={setOrganisationName}
                  inviteUserEmail={inviteUserEmail}
                  setInviteUserEmail={setInviteUserEmail}
                  inviteUserRole={inviteUserRole}
                  setInviteUserRole={setInviteUserRole}
                  orgUsers={orgUsers}
                  setOrgUsers={setOrgUsers}
                />
              )}
            </Tab.Panel>
            <Tab.Panel className="outline-none">
              <IntegrationSettings />
            </Tab.Panel>
            <Tab.Panel className="outline-none">
              <div className="mt-10">
                <InfoPanelContainer
                  title="Employee editing access"
                  description="Grant employees access to edit their own profiles within the Tapt app"
                >
                  <ToggleField
                    label="Grant all employees profile editing access"
                    enabled={
                      preferenceSettings.current.isCardholderEditingEnabled
                    }
                    setter={updateCardholderEditingEnabled}
                  />
                </InfoPanelContainer>

                <InfoPanelDivider />

                <InfoPanelContainer
                  title="Two-way exchange email"
                  description="When enabled, leads collected through your profile will receive an email with your contact details and a link to your profile exactly 24 hours after the exchange as a reminder of the connection. When disabled, no email will be sent."
                >
                  <ToggleField
                    label="Enable two-way exchange email"
                    enabled={preferenceSettings.current.isTwoWayEmailEnabled}
                    setter={updateTwoWayEmailEnabled}
                  />
                </InfoPanelContainer>

                <InfoPanelFooter>
                  <div className="flex justify-end">
                    <Button
                      buttonText="Discard changes"
                      className="mr-3"
                      kind={BUTTON_KIND.WHITE}
                      onClick={discardPreferenceChanges}
                    />
                    <Button
                      buttonText="Save"
                      onClick={savePreferenceSettings}
                    />
                  </div>
                </InfoPanelFooter>
              </div>
            </Tab.Panel>
            <Tab.Panel className="outline-none">
              <div className="mt-10">
                <ManagePlanTab />
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      )}

      {/* Global Plan Required Modal - only available for inactive subscriptions */}
      {isInactive && (
        <PlanRequiredModal
          isOpen={isPlanRequiredModalOpen}
          setIsOpen={setIsPlanRequiredModalOpen}
          checkoutUrl={checkoutUrl}
        />
      )}
    </Layout>
  );
}
